import React, { useEffect, useMemo, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { createSetupIntent } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { getStripe } from '@/services/stripe';
import Spinner from '@/components/Spinner';
import ErrorBanner from '@/components/ErrorBanner';

/**
 * Registers a confirm(returnUrl) function with the parent so the single
 * "Turn on Auto-Pay" CTA (which lives OUTSIDE the Elements provider) can
 * drive stripe.confirmSetup. Returns Stripe's own {setupIntent}/{error}.
 */
const ConfirmBridge = ({ onBridge }) => {
	const stripe = useStripe();
	const elements = useElements();

	useEffect(() => {
		if (!stripe || !elements) return undefined;

		onBridge((returnUrl) =>
			stripe.confirmSetup({
				elements,
				confirmParams: { return_url: returnUrl },
				redirect: 'if_required',
			})
		);

		return () => onBridge(null);
	}, [stripe, elements, onBridge]);

	return null;
};

/**
 * Embedded new-card entry (Figma 2b — card fields stay inside Stripe's
 * iframe). Mints a SetupIntent on the school's Connect account and mounts
 * PaymentElement; the school's payment-method configuration decides what
 * shows (ACH appears automatically when the school enables it).
 */
const NewCardElement = ({ studentId, stripeAccountId, onBridge }) => {
	const [clientSecret, setClientSecret] = useState(null);
	const [error, setError] = useState(null);

	const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
	const stripePromise = useMemo(
		() => (publishableKey && stripeAccountId ? getStripe(publishableKey, stripeAccountId) : null),
		[publishableKey, stripeAccountId]
	);

	useEffect(() => {
		let cancelled = false;
		setClientSecret(null);
		setError(null);

		createSetupIntent(studentId)
			.then((res) => {
				if (!cancelled) setClientSecret(res.client_secret);
			})
			.catch((err) => {
				if (!cancelled) setError(parseHostedError(err).message);
			});

		return () => {
			cancelled = true;
		};
	}, [studentId]);

	if (!publishableKey || !stripeAccountId) {
		return <ErrorBanner>Payments aren’t configured for this school yet.</ErrorBanner>;
	}
	if (error) return <ErrorBanner>{error}</ErrorBanner>;
	if (!clientSecret) return <Spinner label="Loading secure card form…" />;

	return (
		<Elements
			stripe={stripePromise}
			options={{
				clientSecret,
				appearance: {
					theme: 'stripe',
					variables: {
						colorPrimary: '#676AF6',
						fontFamily: 'Inter, sans-serif',
						borderRadius: '10px',
					},
				},
				fonts: [{ cssSrc: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600' }],
			}}
		>
			<PaymentElement options={{ layout: 'tabs' }} />
			<ConfirmBridge onBridge={onBridge} />
		</Elements>
	);
};

export default NewCardElement;
